import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { agentService } from '../services/agentService';
import { useWebRTC } from '../contexts/WebRTCContext';
import { Campaign } from '../types/campaign';

export function isManualDialCampaign(campaign?: {
  campaignType?: string;
  campaign_type?: string;
  dialingType?: string;
  dialing_type?: string;
} | null): boolean {
  if (!campaign) return false;
  const type = campaign.campaignType ?? campaign.campaign_type;
  const dialing = campaign.dialingType ?? campaign.dialing_type;
  return type === 'human' && dialing === 'manual';
}

/** View Results recall — human or bot pipeline leads (not SMS). */
export function isViewResultsDialableCampaign(campaign?: {
  campaignType?: string;
  campaign_type?: string;
} | null): boolean {
  if (!campaign) return false;
  const type = campaign.campaignType ?? campaign.campaign_type;
  return type === 'human' || type === 'bot';
}

export function useDialLead() {
  const { status: webrtcStatus, makeOutboundCall } = useWebRTC();
  const [dialingLeadIds, setDialingLeadIds] = useState<Set<string>>(new Set());

  const dialLead = useCallback(
    async (
      leadId: string,
      campaignId: string,
      displayName?: string | null,
      options?: { resultsCallback?: boolean }
    ) => {
      if (webrtcStatus !== 'ready' && webrtcStatus !== 'in-call') {
        toast.error('WebRTC not connected. Please wait for connection or refresh the page.');
        return;
      }

      setDialingLeadIds((prev) => new Set(prev).add(leadId));
      try {
        const data = await agentService.prepareDial(leadId, campaignId, {
          resultsCallback: options?.resultsCallback,
        });
        makeOutboundCall({
          destinationNumber: data.fromNumber,
          callerNumber: data.fromNumber,
          callAttemptId: data.callAttemptId,
          routingAttemptId: data.routingAttemptId,
          routingLegId: data.routingLegId,
          campaignId: data.campaignId,
          leadId: data.leadId,
          leadName: data.leadName,
          leadPhone: data.leadPhone || data.toNumber,
          leadEmail: data.leadEmail || null,
          companyName: data.companyName || null,
          campaignName: data.campaignName || null,
          scriptName: data.scriptName || null,
          scriptContent: data.scriptContent || null,
          scriptStructure: data.scriptStructure || null,
          agentId: data.agentId,
          leadStatus: data.leadStatus || null,
          preDialStatus: data.preDialStatus || null,
          subStageId: data.subStageId || null,
          dispositionMode: 'results_callback',
        });
        toast.success(`Dialing ${displayName || 'lead'}...`);
      } catch (error: unknown) {
        const err = (error as { response?: { data?: { error?: string | { message?: string } } }; message?: string })
          ?.response?.data?.error;
        const message =
          typeof err === 'string'
            ? err
            : (err as { message?: string })?.message ||
              (error as Error)?.message ||
              'Failed to dial lead';
        toast.error(message);
      } finally {
        setDialingLeadIds((prev) => {
          const next = new Set(prev);
          next.delete(leadId);
          return next;
        });
      }
    },
    [makeOutboundCall, webrtcStatus]
  );

  const isDialing = useCallback((leadId: string) => dialingLeadIds.has(leadId), [dialingLeadIds]);

  return { dialLead, isDialing };
}

export function findCampaignForDial(
  campaigns: Campaign[],
  campaignId?: string | null
): Campaign | undefined {
  if (!campaignId) return undefined;
  return campaigns.find((c) => c.id === campaignId);
}
